import { useMemo, useState } from 'react'
import { Calculator, CheckCircle2, Save } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { Table } from '@/components/ui/table'
import { Combobox } from '@/components/Combobox'
import { FormField } from '@/components/FormField'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { formatCurrencyPrecise, formatDate } from '@/lib/locale'
import { cn } from '@/lib/cn'
import {
  useCalculateReconciliation,
  useReconcileAccounts,
  useReconcileCustomerPayments,
  useReconcileVendorPayments,
  useReconcileHistory,
  useSubmitReconciliation,
} from '@/features/reconcile/queries'
import type { CalculateReconciliationResponse } from '@/features/reconcile/types'

function fmt(v: number | string | null | undefined): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  if (!Number.isFinite(n)) return formatCurrencyPrecise(0)
  return formatCurrencyPrecise(n)
}

export function ReconcilePage() {
  const [accountNo, setAccountNo] = useState<string | null>(null)
  const [date, setDate] = useState('')
  const [actual, setActual] = useState('')
  const [createAdj, setCreateAdj] = useState(false)
  const [calc, setCalc] = useState<CalculateReconciliationResponse | null>(null)

  const { toast } = useToast()
  const accountsQ = useReconcileAccounts()
  const historyQ = useReconcileHistory(accountNo ?? undefined)
  const calculate = useCalculateReconciliation()
  const submit = useSubmitReconciliation()

  // Phase 2e - pull customer_payments for the 90-day window ending on the
  // statement date so the reconciler can spot-check whether each AR
  // payment matches a bank_statement row. Only runs once a date has been
  // entered - we don't want to load every historic payment on mount.
  const paymentWindowFrom = useMemo(() => {
    if (!date) return null
    const d = new Date(date)
    if (Number.isNaN(d.getTime())) return null
    d.setDate(d.getDate() - 90)
    return d.toISOString().slice(0, 10)
  }, [date])
  const customerPaymentsQ = useReconcileCustomerPayments(paymentWindowFrom, date || null, !!(date && paymentWindowFrom))
  const vendorPaymentsQ = useReconcileVendorPayments(paymentWindowFrom, date || null, !!(date && paymentWindowFrom))

  const accountOptions = useMemo(
    () =>
      (accountsQ.data ?? []).map((a) => ({
        value: a.account_no,
        label: `Account ${a.account_no}`,
      })),
    [accountsQ.data],
  )

  const actualNum = parseFloat(actual)
  const diff = calc ? Math.round((actualNum - calc.systemBalance) * 100) / 100 : 0
  const isBalanced = calc ? Math.abs(diff) < 0.01 : false

  async function handleCalculate() {
    if (!accountNo) {
      toast({ title: 'Account required', description: 'Please select a bank account.', variant: 'destructive' })
      return
    }
    if (!date) {
      toast({ title: 'Date required', description: 'Enter the bank statement ending date.', variant: 'destructive' })
      return
    }
    if (!Number.isFinite(actualNum)) {
      toast({
        title: 'Balance required',
        description: 'Enter the ending balance from your bank statement.',
        variant: 'destructive',
      })
      return
    }
    try {
      const data = await calculate.mutateAsync({ accountNo, date })
      setCalc(data)
    } catch (err) {
      toast({
        title: 'Calculation failed',
        description:
          err instanceof Error
            ? err.message
            : 'Ensure transactions are posted to the General Ledger. Go to Financial Reports and click "Post Now" first.',
        variant: 'destructive',
      })
    }
  }

  async function handleSubmit() {
    if (!accountNo || !date || !calc) return
    try {
      const res = await submit.mutateAsync({
        account_no: accountNo,
        date,
        actual_balance: actual,
        system_balance: calc.systemBalance,
        create_adjustment: createAdj,
      })
      toast({
        title: res.isBalanced ? 'Account balanced' : 'Reconciliation saved',
        description: res.isBalanced
          ? 'Bank statement matches the system balance.'
          : `Variance of ${fmt(res.difference)}${createAdj ? ' - adjustment posted to suspense.' : ' - no adjustment created.'}`,
      })
      setCalc(null)
      setActual('')
      setCreateAdj(false)
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Check your connection and try again.',
        variant: 'destructive',
      })
    }
  }

  const history = historyQ.data?.reconciliations ?? []

  return (
    <Page
      title="Reconciliation"
      description="Compare your bank statement ending balance with the system balance to identify discrepancies."
      width="default"
    >
      <Card className="p-4 sm:p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            id="rec-acct"
            label="Select Account"
            control={
              <Combobox
                value={accountNo}
                onChange={(v) => setAccountNo(v as string | null)}
                options={accountOptions}
                placeholder={accountsQ.isLoading ? 'Loading accounts…' : '-- Select Account --'}
              />
            }
          />
          <FormField
            id="rec-date"
            label="Statement End Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.currentTarget.value)}
          />
        </div>
        <div className="mt-3">
          <FormField
            id="rec-actual"
            label="Actual Bank Statement Balance ($)"
            type="number"
            step="0.01"
            value={actual}
            onChange={(e) => setActual(e.currentTarget.value)}
            placeholder="e.g. 10500.00"
          />
        </div>
        <div className="mt-4">
          <Button
            type="button"
            variant="primary"
            className="w-full"
            onClick={handleCalculate}
            disabled={calculate.isPending}
          >
            <Calculator /> Calculate Difference
          </Button>
        </div>

        {calc ? (
          <div className="mt-5 space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-border bg-muted/40 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  System Balance
                </div>
                <div className="mt-1 text-lg font-semibold">{fmt(calc.systemBalance)}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {calc.transactionCount} transactions through {date}
                </div>
              </div>
              <div
                className={cn(
                  'rounded-md border p-3',
                  isBalanced
                    ? 'border-success/50 bg-success/10 text-success'
                    : 'border-destructive/50 bg-destructive/10 text-destructive',
                )}
              >
                <div className="text-xs font-semibold uppercase tracking-wide opacity-80">
                  Difference (Variance)
                </div>
                <div className="mt-1 text-lg font-semibold">{fmt(diff)}</div>
              </div>
            </div>

            {calc.lastReconciliation ? (
              <Alert>
                <AlertDescription className="text-sm">
                  Last reconciled:{' '}
                  <strong>{formatDate(calc.lastReconciliation.statement_date)}</strong> - Bank:{' '}
                  {fmt(calc.lastReconciliation.actual_balance)}, Difference:{' '}
                  {fmt(calc.lastReconciliation.difference)}
                </AlertDescription>
              </Alert>
            ) : null}

            {isBalanced ? (
              <Alert className="border-success/50 bg-success/10 text-success">
                <AlertDescription className="flex items-center gap-2 font-medium">
                  <CheckCircle2 className="size-4" /> Account is balanced. No adjustment needed.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="rounded-md border border-warning/50 bg-warning/10 p-3">
                <p className="font-medium text-warning-foreground">
                  The system does not match the bank statement.
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  <strong>Positive difference</strong> = bank has more than system (unrecorded deposit
                  or interest). <strong>Negative difference</strong> = bank has less than system
                  (unrecorded fee or cleared check).
                </p>
                <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={createAdj}
                    onCheckedChange={(v) => setCreateAdj(v === true)}
                  />
                  Create an adjustment transaction to fix this difference?
                </label>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                type="button"
                variant="primary"
                onClick={handleSubmit}
                disabled={submit.isPending}
              >
                <Save /> Save Reconciliation
              </Button>
            </div>
          </div>
        ) : null}
      </Card>

      {date && customerPaymentsQ.data?.payments && customerPaymentsQ.data.payments.length > 0 ? (
        <Card className="p-4 sm:p-5">
          <h3 className="mb-2 text-base font-semibold sm:text-lg">Customer payments received (last 90 days)</h3>
          <p className="mb-3 text-xs text-muted-foreground">
            Incoming AR payments up to the statement end date. Cross-reference each against the bank feed to confirm it settled - anything listed here that&apos;s missing from the bank statement is the likely source of a variance.
          </p>
          <div className="overflow-x-auto rounded-md border">
            <Table className="w-full min-w-[720px] text-sm">
              <thead className="bg-primary-gradient text-xs uppercase tracking-wide text-primary-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Received</th>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left">Invoice</th>
                  <th className="px-3 py-2 text-left">Method</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {customerPaymentsQ.data.payments.map((p) => (
                  <tr key={p.id} className="border-t">
                    <td className="px-3 py-2">{formatDate(p.received_at)}</td>
                    <td className="px-3 py-2">{p.customer_display_name}</td>
                    <td className="px-3 py-2">{p.invoice_number ?? <span className="text-muted-foreground">-</span>}</td>
                    <td className="px-3 py-2">
                      {p.method}
                      {p.provider ? <span className="ml-1 text-xs text-muted-foreground">({p.provider})</span> : null}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card>
      ) : null}

      {date && vendorPaymentsQ.data?.payments && vendorPaymentsQ.data.payments.length > 0 ? (
        <Card className="p-4 sm:p-5">
          <h3 className="mb-2 text-base font-semibold sm:text-lg">Vendor payments sent (last 90 days)</h3>
          <p className="mb-3 text-xs text-muted-foreground">
            Outgoing AP payments up to the statement end date. Cross-reference each against the bank feed to confirm it cleared - anything listed here that&apos;s missing on the bank statement may indicate a delayed wire/ACH.
          </p>
          <div className="overflow-x-auto rounded-md border">
            <Table className="w-full min-w-[720px] text-sm">
              <thead className="bg-primary-gradient text-xs uppercase tracking-wide text-primary-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Paid</th>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-left">Bill</th>
                  <th className="px-3 py-2 text-left">Method</th>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {vendorPaymentsQ.data.payments.map((p) => (
                  <tr key={p.id} className="border-t">
                    <td className="px-3 py-2">{formatDate(p.paid_at)}</td>
                    <td className="px-3 py-2">{p.vendor_display_name}</td>
                    <td className="px-3 py-2">{p.bill_number ?? <span className="text-muted-foreground">-</span>}</td>
                    <td className="px-3 py-2">
                      {p.method}
                      {p.provider ? <span className="ml-1 text-xs text-muted-foreground">({p.provider})</span> : null}
                    </td>
                    <td className="px-3 py-2">{p.reference ?? <span className="text-muted-foreground">-</span>}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card>
      ) : null}

      <Card className="p-4 sm:p-5">
        <h3 className="mb-3 text-base font-semibold sm:text-lg">Reconciliation History</h3>
        {historyQ.isLoading ? (
          <LoadingSpinner />
        ) : history.length === 0 ? (
          <EmptyState
            title={accountNo ? 'No reconciliations for this account yet.' : 'Select an account to see history'}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table className="w-full min-w-[720px] text-sm">
              <thead className="bg-primary-gradient text-xs uppercase tracking-wide text-primary-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Account</th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-right">Bank Balance</th>
                  <th className="px-3 py-2 text-right">System Balance</th>
                  <th className="px-3 py-2 text-right">Difference</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((r, i) => {
                  const d = parseFloat(String(r.difference)) || 0
                  const balanced = Math.abs(d) < 0.01
                  return (
                    <tr key={r.id ?? i} className="border-t">
                      <td className="px-3 py-2">{r.account_name}</td>
                      <td className="px-3 py-2">{formatDate(r.statement_date)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(r.actual_balance)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(r.system_balance)}</td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right font-mono font-medium',
                          balanced ? 'text-success' : 'text-destructive',
                        )}
                      >
                        {fmt(d)}
                      </td>
                      <td className="px-3 py-2">
                        <span className={cn('font-semibold', balanced ? 'text-success' : 'text-destructive')}>
                          {balanced ? 'Balanced' : `Off by ${fmt(d)}`}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </Page>
  )
}
